import React, { useState, useEffect, useRef } from 'react';
import { Select, Button, Modal, Input, message, Space, Row, Col, Typography, DatePicker } from 'antd';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Toolbar,
  Edit,
  Page,
  ExcelExport,
  PdfExport,
} from '@syncfusion/ej2-react-grids';
import styles from "./styles.module.css";
import { EditSettingsModel } from '@syncfusion/ej2-react-grids';
import { Calendar } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { registerLicense } from '@syncfusion/ej2-base';
const baseURL = `${process.env.REACT_APP_BACKEND_URL || ""}api/`;


const { Title } = Typography;

interface Holiday {
  holiday_id: number;
  holiday_date: string;
  holiday_name: string;
}

const HolidayCalendarTab: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [years, setYears] = useState<number[]>([2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030]); // Initial list of years
  const [selectedYear, setSelectedYear] = useState<number>(2024);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [yearModalVisible, setYearModalVisible] = useState(false);
  const [newYear, setNewYear] = useState<string>('');
  const [holidayName, setHolidayName] = useState<string>('');
  const [holidayDate, setHolidayDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingHolidayId, setEditingHolidayId] = useState<number | null>(null);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);

  const gridRef = useRef<GridComponent>(null);


  registerLicense('Ngo9BigBOggjHTQxAR8/V1NCaF1cVGhIfEx1RHxQdld5ZFRHallYTnNWUj0eQnxTdEFjUX5XcHVVQ2NfUUR2Vg==');
  const token = localStorage.getItem('cxspsa') || '';

  const editOptions: EditSettingsModel = { allowEditing: true, allowAdding: true, allowDeleting: true, mode: 'Dialog' };

  // Check if a date already has a holiday
  const isDateBooked = (date: Dayjs, excludeHolidayId?: number): boolean => {
    return holidays.some(holiday =>
      holiday.holiday_date === date.format('YYYY-MM-DD') &&
      holiday.holiday_id !== excludeHolidayId
    );
  };

  const fetchData = async (year: number) => {
    try {
      const response = await fetch(baseURL + `holidays/${year}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const result = await response.json(); // Assuming the API returns JSON data
      setHolidays(result); // Update state with the fetched data
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    // Fetch holiday data from the API for the selected year
    // setHolidays(fetchedData); // Update holidays state with data from backend
  };

  useEffect(() => {
    fetchData(selectedYear);
  }, [selectedYear]);

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
    fetchData(value);
  };

  const openAddYearModal = () => {
    setYearModalVisible(true);
  };

  const handleAddYear = () => {
    setLoading(true);
    const parsedYear = parseInt(newYear, 10);
    if (isNaN(parsedYear) || parsedYear < 1900 || parsedYear > 2100) {
      message.error('Please enter a valid year between 1900 and 2100');
      return;
    }
    if (!years.includes(parsedYear)) {
      setYears([...years, parsedYear]);
      setSelectedYear(parsedYear);
      fetchData(parsedYear);
      message.success(`Year ${parsedYear} added successfully`);
      setLoading(false);
    } else {
      message.info(`Year ${parsedYear} already exists`);
    }
    setYearModalVisible(false);
    setNewYear('');
  };
  // Disable dates outside the selected year range
  const disableDatesOutsideSelectedYear = (currentDate: any) => {
    // Disable dates that are not in the selected year
    return currentDate.year() !== selectedYear;
  };

  const openAddDialog = () => {
    setDialogVisible(true);
  };

  const openCalendarDialog = () => {
    setCalendarVisible(true);
  };

  const handleSaveHoliday = async () => {
    if (!holidayName || !holidayDate) {
      message.error('Please fill in all required fields');
      return;
    }

    // Check if the selected date already has a holiday
    if (isDateBooked(holidayDate, editingHolidayId)) {
      message.error('A holiday already exists on this date. Please select a different date.');
      return;
    }
    // if (holidayName && holidayDate) {
    const newHoliday = {
      holiday_id: editingHolidayId,
      holiday_date: holidayDate.format('YYYY-MM-DD'),
      holiday_name: holidayName,
      holiday_year: selectedYear,
    };
    if (isEditing) {
      try {
        setLoading(true);
        const response = await fetch(baseURL + `holidays/edit/${editingHolidayId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }, body: JSON.stringify(newHoliday),
        });

        if (response.ok) {
          const index = holidays.findIndex((holiday) => holiday.holiday_id === newHoliday.holiday_id);

          if (index !== -1) {
            // Replace the old holiday with the updated one in the holidays array
            const updatedHolidays = [
              ...holidays.slice(0, index),
              newHoliday,
              ...holidays.slice(index + 1)
            ];

            // Update the state with the modified holidays array
            setHolidays(updatedHolidays);
          } else {
            // If the holiday is new, add it to the array
            setHolidays([...holidays, newHoliday]);
          }

          // setHolidays([...holidays, newHoliday]);
          setDialogVisible(false);
          setHolidayName('');
          setHolidayDate(null);
          message.success(`Holiday ${isEditing ? 'updated' : 'added'} successfully`);

          // const newData = await fetchData(selectedValue);  // Re-fetch all data after update
        } else {
          console.error('Failed to save the data.');
        }
      } catch (error) {
        console.error('Error while saving the data:', error);
      } finally {
        setLoading(false);
      }

    } else {
      try {
        setLoading(true);
        const response = await fetch(baseURL + 'holidays', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }, body: JSON.stringify(newHoliday),
        });

        if (response.ok) {
          setHolidays([...holidays, newHoliday]);
          setDialogVisible(false);
          setHolidayName('');
          setHolidayDate(null);
          message.success(`Holiday ${isEditing ? 'updated' : 'added'} successfully`);

          // const newData = await fetchData(selectedValue);  // Re-fetch all data after update
        } else {
          console.error('Failed to save the data.');
        }
      } catch (error) {
        console.error('Error while saving the data:', error);
      } finally {
        setLoading(false);
      }

    }

    // }
  };

  const handleRowEdit = (record: Holiday) => {
    setHolidayName(record.holiday_name);
    setHolidayDate(dayjs(record.holiday_date));
    setSelectedHoliday(record);
    // setIsEditing(true);
    // setEditingHolidayId(record.holiday_id);
    // setDialogVisible(true);
    setCalendarVisible(true); // Open calendar modal to view/edit holidays in a calendar view
  };

  const getDateCellStyle = (date: Dayjs) => {
    const formattedDate = date.format('YYYY-MM-DD');

    if (selectedHoliday && selectedHoliday.holiday_date === formattedDate) {
      return {
        backgroundColor: '#1890ff',
        borderRadius: '50%',
        color: 'white'
      };
    }
    const holiday = holidays.find((h) => h.holiday_date === formattedDate);
    if (holiday) {
      return {
        color: 'red'
      };
    }

    return {};
  };
  const dateCellRender = (date: Dayjs) => {
    const formattedDate = date.format('YYYY-MM-DD');
    const holiday = holidays.find((h) => h.holiday_date === formattedDate);
    const isSelectedDate = selectedHoliday && selectedHoliday.holiday_date === formattedDate;

    if (holiday || isSelectedDate) {
      return (
        <div
          style={{
            height: '100%',
            padding: '4px',
            ...getDateCellStyle(date)
          }}
        >
          {holiday?.holiday_name}
        </div>
      );
    }
    return null;
  };



  const actionBegin = async (args: any) => {
    if (args.requestType === 'beginEdit') {
      setHolidayName(args.rowData.holiday_name);
      setHolidayDate(dayjs(args.rowData.holiday_date));
      setIsEditing(true);
      setEditingHolidayId(args.rowData.holiday_id);
      setDialogVisible(true);
      setDialogVisible(true);
      args.cancel = true;
    }
    if (args.requestType === 'add') {
      setEditingHolidayId(null);
      setDialogVisible(true);
      setIsEditing(false);
      args.cancel = true;
    }
    if (args.requestType === 'delete') {
      const { data } = args;
      const holidayIdToDelete = data[0].holiday_id; // Assuming only one row is selected for deletion

      Modal.confirm({
        title: 'Are you sure you want to delete this holiday?',
        content: `Holiday: ${data[0].holiday_name} on ${data[0].holiday_date}`,
        okText: 'Yes',
        cancelText: 'No',
        async onOk() {
          try {
            const response = await fetch(`${baseURL}holidays/delete/${holidayIdToDelete}`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });

            if (response.ok) {
              setHolidays(holidays.filter((holiday) => holiday.holiday_id !== holidayIdToDelete));
              message.success('Holiday deleted successfully');
            } else {
              message.error('Failed to delete holiday');
            }
          } catch (error) {
            console.error('Error deleting holiday:', error);
            message.error('An error occurred while deleting the holiday');
          }
        },
        onCancel() {
          args.cancel = true;
        },
      });
    }

  };
  const handleDialogClose = () => setDialogVisible(false);
  const toolbarOptions = ['Add', 'Edit', 'Delete', 'ExcelExport', 'PdfExport',];
  const toolbarClick = (args: any) => {
    console.log(args.item.properties.id);
    if (gridRef && args.item.properties.id?.includes('excelexport')) {
      gridRef.current?.excelExport({
        fileName: `Holidays_${selectedYear}.xlsx`,
        theme: {
          header: { bold: true, fontColor: '#454545', fontName: 'Calibri', fontSize: 10 },
          record: { fontColor: '#1D1D1D', fontName: 'Calibri', fontSize: 8 }
        }
      });
    } else if (gridRef && args.item.properties.id?.includes('pdfexport')) {
      gridRef.current?.pdfExport({
        fileName: `Holidays_${selectedYear}.pdf`,
        theme: {
          header: { bold: true, fontColor: '#454545', fontName: 'Calibri', fontSize: 10 },
          record: { fontColor: '#1D1D1D', fontName: 'Calibri', fontSize: 8 }
        }
      });
    }
  };

  return (
    <div className={styles.container}>
      <Space direction="vertical" size="large" className={styles.main}>
        <Space direction="vertical" size="small" className={styles.main + ' ' + styles.spaceItem}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4}>Manage Holidays</Title>
            </Col>
            <Col>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                options={years.map(year => ({ value: year, label: year.toString() }))}
                style={{ width: 120 }}
              />
              <Button onClick={openAddYearModal} type="primary" style={{ marginLeft: '10px' }} disabled={loading}>
                {loading ? 'Adding ...' : 'Add Year'}
              </Button>
            </Col>
          </Row>

          <GridComponent
            ref={gridRef}
            dataSource={holidays}
            toolbar={toolbarOptions}
            editSettings={editOptions}
            actionBegin={actionBegin}
            allowPaging={true}
            pageSettings={{ pageSize: 10 }}
            allowExcelExport={true}
            allowPdfExport={true}
            toolbarClick={toolbarClick}
          // toolbarClick={openAddDialog}
          >
            <ColumnsDirective>
              <ColumnDirective field="holiday_id" headerText="ID" width="100" textAlign="Right" isPrimaryKey={true}  visible={false} />
              <ColumnDirective field="holiday_name" headerText="Holiday Name" width="200" />
              <ColumnDirective field="holiday_date" headerText="Date" width="200" format="yMd" />
              <ColumnDirective
                headerText="Actions"
                width="150"
                template={(record: any) => (
                  <Space size="middle">
                    <a onClick={() => handleRowEdit(record)}>View</a>
                  </Space>
                )}
              />
            </ColumnsDirective>
            <Inject services={[Edit, Page, Toolbar, ExcelExport, PdfExport,]} />
          </GridComponent>
        </Space>

        {/* Add Holiday Modal */}
        <Modal
          title={isEditing ? "Edit Holiday" : "Add Holiday"}
          open={dialogVisible}
          onOk={handleSaveHoliday}
          onCancel={() => setDialogVisible(false)}
        >
          <Input
            placeholder="Holiday Name"
            value={holidayName}
            onChange={(e) => setHolidayName(e.target.value)}
            style={{ marginBottom: '10px' }}
          />
          <DatePicker
            disabledDate={(date) => {
              return (
                disableDatesOutsideSelectedYear(date) ||
                (isDateBooked(date, editingHolidayId) && !date.isSame(holidayDate, 'day'))
              );
            }}

            // disabledDate={disableDatesOutsideSelectedYear}
            placeholder="Select Date"
            value={holidayDate}
            onChange={(date) => setHolidayDate(date)}
            style={{ width: '100%' }}
          />
          {holidayDate && isDateBooked(holidayDate, editingHolidayId) && (
            <div style={{ color: '#ff4d4f', marginTop: '8px' }}>
              Warning: A holiday already exists on this date
            </div>
          )}
        </Modal>

        {/* Add Year Modal */}
        <Modal
          title="Add New Year"
          open={yearModalVisible}
          onOk={handleAddYear}
          onCancel={() => setYearModalVisible(false)}
        >
          <Input
            placeholder="Enter year (e.g., 2025)"
            value={newYear}
            onChange={(e) => setNewYear(e.target.value)}
          />
        </Modal>

        {/* Calendar View Modal */}
        <Modal
          title={`Holidays for ${selectedYear}`}
          open={calendarVisible}
          onCancel={() => {
            setCalendarVisible(false);
            setSelectedHoliday(null);
          }}
          footer={null}
          width="50%"
        >
          <div style={{ marginBottom: '16px' }}>
            {selectedHoliday && (
              <div style={{
                padding: '12px',
                background: '#f0f2f5',
                borderRadius: '4px'
              }}>
                <strong>Selected Holiday:</strong> {selectedHoliday.holiday_name} ({dayjs(selectedHoliday.holiday_date).format('MMMM D, YYYY')})
              </div>
            )}
          </div>

          <Calendar
            fullscreen
            value={selectedHoliday ? dayjs(selectedHoliday.holiday_date) : dayjs(`${selectedYear}-01-01`)}
            dateCellRender={dateCellRender}
            mode="month"
          />

        </Modal>
      </Space>
    </div>
  );
};

export { HolidayCalendarTab };

